import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, User, LogOut, ChevronRight, ExternalLink } from "lucide-react";
import { adminAuthService } from "@/api/adminAuthService";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import AdminSidebar from "./AdminSidebar";

/**
 * ============================================================
 * ADMIN TOP HEADER
 * ============================================================
 * Displays breadcrumbs/current location, active admin user,
 * and mobile drawer navigation trigger.
 */
export default function AdminHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const adminUser = adminAuthService.getCurrentAdmin();

  // Compute breadcrumb based on path
  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path === "/admin") return "Dashboard";
    if (path === "/admin/projects") return "Projects";
    if (path === "/admin/projects/new") return "Projects / Create New";
    if (path.startsWith("/admin/projects/") && path.includes("/edit")) return "Projects / Edit Case Study";
    if (path === "/admin/taxonomy") return "Projects / Taxonomy & Tech";
    if (path === "/admin/workshops") return "Workshops";
    if (path === "/admin/workshops/new") return "Workshops / Create New";
    if (path.includes("/edit")) return "Workshops / Edit Workshop";
    if (path.includes("/registrations")) return "Workshops / Student Registrations";
    return "Admin Console";
  };

  const handleLogout = () => {
    adminAuthService.logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-slate-200/80 bg-white/80 backdrop-blur-xl px-4 sm:px-8 flex items-center justify-between shadow-xs">
      {/* Left: Mobile Drawer Trigger + Breadcrumb */}
      <div className="flex items-center gap-3">
        {/* Mobile menu trigger */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="lg:hidden h-9 w-9 p-0 border-slate-200 bg-slate-100/60 text-slate-700 hover:bg-slate-100"
            >
              <Menu className="w-4 h-4" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64 bg-white border-slate-200 text-slate-900">
            <SheetHeader className="sr-only">
              <SheetTitle>Admin Navigation</SheetTitle>
            </SheetHeader>
            <AdminSidebar onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Breadcrumb Trail */}
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-slate-500 hidden sm:inline font-medium">Admin</span>
          <ChevronRight className="w-3.5 h-3.5 text-slate-400 hidden sm:inline" />
          <span className="text-slate-900 font-semibold">{getBreadcrumb()}</span>
        </div>
      </div>

      {/* Right: Admin User Profile & Quick Actions */}
      <div className="flex items-center gap-3">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
        >
          <ExternalLink className="w-3 h-3 text-cyan-600" />
          <span>Live Site</span>
        </a>

        {/* User Identity Pill */}
        <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-slate-50">
          <div className="w-6 h-6 rounded-full bg-cyan-50 border border-cyan-200 flex items-center justify-center text-cyan-600">
            <User className="w-3.5 h-3.5" />
          </div>
          <div className="hidden sm:block text-left">
            <div className="text-xs font-semibold text-slate-900 leading-tight">
              {adminUser?.name || "Operations"}
            </div>
            <div className="text-[10px] text-slate-500 leading-tight font-medium">
              {adminUser?.role || "Administrator"}
            </div>
          </div>
        </div>

        {/* Logout Icon */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="h-9 w-9 p-0 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-xl"
          title="Sign Out"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>
    </header>
  );
}
